"use strict";

import axios, { AxiosError, AxiosResponse } from 'axios';

// import { AMRosterInfo, AMRosterInfoV4, AlertMonitorFullInfo, GetFileInfo } from './definitions';
// import { RFThresholdEntry } from './classes/puStructs';
// import { g_teamID } from './nxttool';
import log from '../utils/logger';
import { Helmet } from '../components/tools/PlayerUnitSwapTool';

// ITT Client identifier for headers
const ITT_CLIENT = 'IST';

// import AlertMonitor from './classes/AlertMonitor';
// TODO: import { allowed_api_post_configs } from './config/config';
const allowed_api_post_configs = 
{
  post_endpoints: [
        'amcFlashDump',
        'amcFlashDumpV4',
        'playerUnitToTeam',
        'postProcess',
        'impact/ipa-params',
        'move-helmet',
        'authenticate',
        'logout'
  ],
};

// Define position IDs for American football team positions
// TODO: Call GET /positions from the API to get this data
export const positions: Record<string, number> = {
  QB: 1,
  RB: 2,
  OL: 3,
  DL: 6,
    'P/K/LS': 7,
  WR: 8,
    'TE/FB': 9,
  OLB: 10,
  ILB: 11,
  CB: 12,
  SAF: 13,
};

export interface PlayerUnitFirmwareBuffers {
  lower: Buffer;
  upper: Buffer;
  version: string;
}

export interface APIError {
  name: string;
  statusCode: number;
  error: {
    error: boolean;
    result: string;
    message: string;
    name: string;
    code?: string;
  };
  options?: {
    method: string;
    uri: string;
  };
}

const http = axios.create();

http.interceptors.response.use((response) => {

  return response;
});

class APIClass {
  private _token: string | number | boolean;
  // server host is now part of the instance, but defaults to the host from the build config.
  // can be changed from superadmin tools
  private _host: string;
  private _logoutHandler: (() => Promise<void>) | null = null;

  constructor() {
        this._token = '';
        this._host = '';
  }

  init(url: string) {
    this._host = url;
  }

  setLogoutHandler(handler: () => Promise<void>) {
    this._logoutHandler = handler;
  }
  private handleApiError(err: AxiosError) {
    log.error(
      `API: Error ${JSON.stringify(err.response?.status)} - ${err.message}`
    );

    // Check for 401 Unauthorized responses
    if (err.response?.status === 401) {
            log.info('API: 401 Unauthorized received, triggering logout');
      if (this._logoutHandler) {
        // Call logout handler asynchronously without waiting
        this._logoutHandler().catch((logoutError) => {
                    console.error('Logout handler failed:', logoutError);
        });
      }
      throw err;
    }

    // can't even reach the server, bail with an alert and throw the error to let higher-level code
    // do any special processing
        if (err.isAxiosError && 'Network Error' === err.message) {
      err.message = `Riddell Equipment Room cannot connect to the ITT server.  Please check your internet connection.`;
      throw err;
    }

    // when all else fails, just throw the error
    throw err;
  }

    private async get(
        endpoint: string, 
        params: Record<string, string | number | boolean> = {}, 
        logEndpoint = true
    ): Promise<any> {
        const queryString = queryFromParams(params);
        const uri = `${this.host}/${endpoint}${queryString}`;
        const httpOptions = {
        headers: {
            Authorization: this._token,
                'Content-Type': 'application/json',
                'x-sbx-itt-client': ITT_CLIENT,
        },
    };

    try {
      const resp = await http.get(uri, httpOptions);
      if (logEndpoint) {
        log.info(`API: GET /${endpoint}${queryString} succeeded`);
      }
      return resp.data;
    } catch (err) {
      const axiosError = err as AxiosError;
      log.info(`API: GET /${endpoint}${queryString} failed: ${err}`);
      this.handleApiError(axiosError);
    }
  }

  private async get_octet_stream(
    endpoint: string,
    params: Record<string, string | number | boolean> = {},
        logEndpoint = true,
  ): Promise<any> {
    const queryString = queryFromParams(params);
    const uri = `${this.host}/${endpoint}${queryString}`;

    try {
      const resp = await http.get(
        uri,
        {
            responseType: 'arraybuffer',
            headers: {
                Authorization: this.token,
                    'Content-Type': 'application/octet-stream',
                    Accept: 'application/octet-stream',
                    'x-sbx-itt-client': ITT_CLIENT,
            },
        } /* httpOptions */,
      );
      if (logEndpoint) {
        log.info(`API: GET /${endpoint}${queryString} succeeded`);
      }
      return resp;
    } catch (err) {
      const axiosError = err as AxiosError;
      log.info(`API: GET /${endpoint}${queryString} failed: ${err}`);
      this.handleApiError(axiosError);
    }
  }

  private async post(
    endpoint: string,
    bodyObj: object = {},
    logEndpoint = true
  ): Promise<any> {
    if (!allowed_api_post_configs.post_endpoints.includes(endpoint)) {
      throw new Error(`User not allowed to use this endpoint: ${endpoint}`);
    }
    const uri = `${this.host}/${endpoint}`;
    const httpOptions = {
      headers: {
        Authorization: this.token,
            'Content-Type': 'application/json',
            'x-sbx-itt-client': ITT_CLIENT,
      },
    };

    try {
      const resp = await http.post(uri, bodyObj, httpOptions);
      if (logEndpoint) {
        log.info(`API: POST /${endpoint} succeeded`);
      }
      return resp.data;
    } catch (err) {
      const axiosError = err as AxiosError;

        if (!['log', 'logout'].includes(endpoint)) {
            log.error(`API: POST /${endpoint} failed: ${axiosError}`);
            this.handleApiError(axiosError);
      }
    }
  }

  /* calls for postform,  put and delete if needed
    private async postform(
        endpoint: string,
        bodyObj: object = {},
        logEndpoint = true,
    ): Promise<any> {
        const httpOptions = {
            method: 'POST',
            uri: `${this.host}/${endpoint}`,
            headers: {
                Authorization: this.token,
                'Content-Type': 'multipart/form-data',
                'x-sbx-itt-client': ITT_CLIENT,
            },
            formData: bodyObj,
            // json: true,
        };

        try {
            const resp = await http.post(httpOptions);
            if (logEndpoint) {
                log.info(`API: POST /${endpoint} succeeded`);
            }
            return resp;
        } catch (err) {
            if (!['log', 'logout'].includes(endpoint)) {
                log.error(`API: POST /${endpoint} failed: ${err}`);
                this.handleApiError(err);
            }
        }
    }
    */
  private async put(
    endpoint: string,
    params: Record<string, string | number | boolean> = {},
    bodyObj: object = {},
    logEndpoint = true,
  ): Promise<any> {
    const queryString = queryFromParams(params);
    const uri = `${this.host}/${endpoint}${queryString}`;
    const httpOptions = {
      headers: {
        Authorization: this.token,
            'Content-Type': 'application/json',
            'x-sbx-itt-client': ITT_CLIENT,
      },
    };

    try {
      const resp = await http.put(uri, bodyObj, httpOptions);
      if (logEndpoint) {
        log.info(`API: PUT /${endpoint}${queryString} succeeded`);
      }
      return resp.data;
    } catch (err) {
      const axiosError = err as AxiosError;
      log.info(`API: PUT /${endpoint}${queryString} failed: ${err}`);
      this.handleApiError(axiosError);
    }
  }

  /*
      private async delete(endpoint: string, params: object = {}, logEndpoint = true): Promise<any> {
        const queryString = queryFromParams(params);
        const uri = `${this.host}/v0/${endpoint}${queryString}`;
        const httpOptions = {
          headers: {
            Authorization: this.token,
            'Content-Type': 'application/json',
            'x-sbx-itt-client': ITT_CLIENT,
          },
          json: true,
        };
    
        try {
          const resp = await http.delete(uri, httpOptions);
          if (logEndpoint) {
            log.info(`API: PUT /${endpoint}${queryString} succeeded`);
          }
          return resp.data;
        } catch (err) {
          log.error(`API: PUT /${endpoint}${queryString} failed: ${err}`);
          this.handleApiError(err);
        }
      }
    */
  /*
    async GetDefaultThresholds(): Promise<RFThresholdEntry> {
        try {
            const apiResp = await this.get('defaultThresholds');
            const defaultThresholds = new RFThresholdEntry();
            defaultThresholds.playerUnitSerialNumber = 0; // used to initialize the array of PU info for Yeti firmware
            defaultThresholds.cumAddThres = apiResp.cumAddThreshold;
            defaultThresholds.cumAlertThres = apiResp.cumAlertThreshold;
            defaultThresholds.dataCollectionThres = apiResp.dataCollectionThreshold;
            defaultThresholds.peakAlertThres = apiResp.peakAlertThreshold;
            return defaultThresholds;
        } catch (err) {
            throw err;
        }
    }

    async GetAMRoster(): Promise<Array<AMRosterInfo>> {
        try {
            const amRosterRes = await this.get('rosterAmConfig', {
                teamID: g_teamID,
            });
            return amRosterRes.rows;
        } catch (err) {
            throw err;
        }
    }

    async GetAMRosterV4(): Promise<Array<AMRosterInfoV4>> {
        try {
            const teamID = g_teamID; // store.getState().User.tokenInfo!.teamID;

            const amRosterRes = await this.get('rosterAmConfigV4', {
                teamID: teamID,
            });
            return amRosterRes.rows;
        } catch (err) {
            throw err;
        }
    }

    async GetTeamsEquipment(): Promise<Array<AlertMonitorFullInfo>> {
        try {
            const teRes = await this.get('teamsEquipment', {
                teamID: g_teamID,
            });
            const AMs = teRes.rows as Array<AlertMonitorFullInfo>;
            log.info(`API: Retrieved data for ${AMs.length} AMs`);

            // overwrite the latestFirmware field (which is used by AMC1.x) with
            // the version appropriate for AMC 2.0
            const latestAMFW = await this.GetLatestAMFirmwareVersion();
            log.info(`API: latest AM Firmware in database: ${latestAMFW}`);
            AMs.map((am) => (am.latestFirmware = latestAMFW));
            AMs.map((am) => (am.firmwareUpToDate = latestAMFW === am.alertMonitorFirmwareString));
            try {
                const rerConfigRes = await this.get('rerConfig');
                AMs.map(
                    (am) =>
                    (am.firmwareAcceptable =
                        rerConfigRes.hideAMFirmwareUpdateWhenInstalledFWMin <=
                        am.alertMonitorFirmwareString &&
                        am.alertMonitorFirmwareString <=
                        rerConfigRes.hideAMFirmwareUpdateWhenInstalledFWMax),
                );
            } catch (err) {
                log.error(`API: GetTeamsEquipment failed: ${err}`);
            }
            return AMs;
        } catch (err) {
            throw err;
        }
    }

    async GetLatestAMFirmwareVersion(): Promise<string> {
        try {
            // rf and ui version are in the same row, so just get one.
            const rfVersion = await this.GetFileInfo({
                clientOS: 'na',
                version: 'latest',
                platform: 'amRFFirmware',
            });
            return rfVersion.version;
        } catch (err) {
            console.log(`GetLatestAMFWVers failed ${err}`);
            throw err;
        }
    }
/*
    /**
     * Returns PlayerUnitFirwmareBuffers (lower image, upper image and version) for requested
     * player unit generaton and version.
     * @param puGen
     * @param puVersion
     */
  /*    async GetPUFirmware(puGen: number, puVersion: string): Promise<PlayerUnitFirmwareBuffers> {
        let puFwBuffers: PlayerUnitFirmwareBuffers = {} as PlayerUnitFirmwareBuffers;

        try {
            // get the lower image for this PU generation
            let fileParams = {
                clientOS: 'na',
                version: puVersion,
                platform: 'playerUnitFWlower',
                generation: puGen,
            };
            let puFwRes = await this.GetFile(fileParams);
            puFwBuffers.lower = puFwRes.body;

            // get the upper image for this PU generation
            fileParams = {
                clientOS: 'na',
                version: puVersion,
                platform: 'playerUnitFWupper',
                generation: puGen,
            };
            puFwRes = await this.GetFile(fileParams);
            puFwBuffers.upper = puFwRes.body;
            puFwBuffers.version = puFwRes.headers['x-sbx-version'];

            return puFwBuffers;
        } catch (err) {
            log.error(`GetPUFirmware failed: ${err}`);
            throw err;
        }
    }
*/
  /**
   * Returns a FULL HTTP response (body AND headers) so we can read the
   * headers which contains version and filename information.  The required
   * image is in the body.
   * @param params
   */
  /*    async GetFile(params: Object): Promise<any> {
        try {
            const fullResp = await this.get_octet_stream('file', params);
            log.info(`API: GetFile retrieved ${fullResp.headers['file-name']}`);
            return fullResp;
        } catch (err) {
            // Pull the PG error out of the API response
            err.message = JSON.parse(err.error).error;
            throw err;
        }
    }

    async GetFileInfo(params: Object): Promise<GetFileInfo> {
        try {
            return await this.get('fileInfo', params);
        } catch (err) {
            throw err;
        }
    }

    async getIpaParams(body: Object): Promise<any> {
        try {
            const res = await this.get('impact/ipa-params', body);
            return res;
        } catch (err) {
            throw err;
        }
    }

    async GetAmSerialFromPCBA(pcba_serial_number: string): Promise<string> {
        try {
            return await this.get(`alertMonitorSerialString/${pcba_serial_number}`);
        } catch (err) {
            throw err;
        }
    }
*/
    async PostPlayerUnitToTeam(body: any): Promise<any> {
        log.info(`PU: Assigning ${body.playerUnitSerialString} to team ${body.teamID}`);
        try {
            const resp = await this.post(`playerUnitToTeam`, body);
            return resp;
        } catch (err) {
            throw err;
        }
    }

    async GetUnassignedPlayerUnits(teamID: number): Promise<any> {
        log.info(`PU: Retrieving unassigned player units for team ${teamID}`);
        try {
            return await this.get(`unassignedPlayerUnits?teamID=${teamID}`);
        } catch (err) {
            throw err;
        }
    }

    async GetPlayerUnitBySerial(playerUnitSerialString: string): Promise<any> {
        log.info(`PU: Retrieving player unit details for serial ${playerUnitSerialString}`);
        try {
            return await this.get(`playersEquipment?playerUnitSerialString=${playerUnitSerialString}`);
        } catch (err) {
            throw err;
        }
    }

  async PostPlayersTeamsEquipment(body: any): Promise<any> {
    try {
      return await this.post(`playersTeamsEquipment`, body);
    } catch (err) {
      throw err;
    }
  }

    async PutPlayersTeamsEquipment(body: any): Promise<any> {
        try {
            return await this.put(`playersTeamsEquipment`, {}, body);
        } catch (err) {
            throw err;
        }
    }

    async EndPlayersEquipment(playersEquipmentID: number): Promise<any> {
        try {
            return await this.put(`endPlayersEquipment/${playersEquipmentID}`);
        } catch (err) {
            throw err;
        }
    }   



/*    async SendFlashDump(am: AlertMonitor, type: string, data: Buffer, teamID: number): Promise<any> {
        const data_base64: string = data.toString('base64');
        // const teamID = store.getState().User.tokenInfo!.teamID;

        const uploadObj = {
            alertMonitorSerialString: am.alertMonitorSerialString,
            amcVersion: '4.x.x.x', //app.getVersion(), // 4.x.x.x is NXT RSM version
            alertMonitorFirmwareVersion: am.alertMonitorFirmwareVersion,
            alertMonitorFlashDataType: type,
            amFlashDump: data_base64,
            teamID: teamID,
        };

        try {
            return await this.post('amcFlashDump', uploadObj);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Retrieves information about a team.
     * @param teamID - The ID of the team to retrieve information for.
     * @returns A Promise that resolves to the team information.
     * @throws If an error occurs while retrieving the team information.
     */
    async GetOrgs(): Promise<any> {
        try {
            const result = await this.get('orgs');
            if (!result.rowCount || result.rowCount === 0) {
                throw new Error(`No organizations found`);
            }
            const api_result = { success: true, data: result.rows };
            return api_result
        } catch (err) {
            throw err;
        }
    }    

    /**
     * Retrieves teams for a specific organization.
     * @param orgID - The ID of the organization to retrieve teams for.
     * @returns A Promise that resolves to the teams information.
     * @throws If an error occurs while retrieving the teams information.
     */
    async GetOrgTeams(orgID: string | number): Promise<any> {
        try {
            const result = await this.get(`orgs/${encodeURIComponent(orgID)}/teams`);
            if (!result.rowCount || result.rowCount === 0) {
                throw new Error(`No teams found for organization ${orgID}`);
            }
            const api_result = { success: true, data: result.rows };
            return api_result;
        } catch (err) {
            throw err;
        }
    }

  /**
   * Retrieves helmet information by helmet ID (RFID label or code).
   * Supports both YY-#### format (labels) and alphanumeric format (codes).
   * @param helmetId - The helmet identifier (RFID label or code)
   * @returns A Promise that resolves to helmet information (single object or array for disambiguation)
   * @throws If an error occurs while retrieving the helmet information.
   */
  async GetHelmetInfo(helmetId: string): Promise<any> {
    try {
      const result = await this.get(
        `helmets?helmet=${encodeURIComponent(helmetId)}`
      );
      if (!result) {
        throw new Error(`Helmet ${helmetId} not found`);
      }
      const api_result = { success: true, data: result };
      return api_result;
    } catch (err) {
      throw err;
    }
  }

    /**
     * Retrieves helmet information for a team by teamID.
     * @param teamID - The team ID to retrieve helmets for
     * @returns A Promise that resolves to helmet information array
     * @throws If an error occurs while retrieving the helmet information.
     */
    async GetHelmets(teamID: number): Promise<any> {
        try {
            const queryParam = `helmets?teamID=${encodeURIComponent(teamID)}`;
            
            const result = await this.get(queryParam);
            if (!result) {
                throw new Error(`No helmets found for team ${teamID}`);
            }
            
            const api_result = { success: true, data: result };
            return api_result;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Updates helmet information for a specific player unit.
     * @param helmetInfo - The helmet information to update.
     * @param playerUnitID - The ID of the player unit to associate with the helmet.
     * @returns A Promise that resolves to the result of the API call.
     * @throws If an error occurs during the API call.
     */
    async PutHelmet(helmetInfo: Helmet, playerUnitSerialString: string): Promise<any> {
        try {
            const helmetBody = { 
                ...helmetInfo,
                playerUnitSerialString: playerUnitSerialString,
                helmet_config_id: null
            };

            return await this.put(`helmet`, {}, helmetBody);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Retrieves information about a team.
     * @param teamID - The ID of the team to retrieve information for.
     * @returns A Promise that resolves to the team information.
     * @throws If an error occurs while retrieving the team information.
     */
    async GetTeam(teamID: number): Promise<any> {
        try {
            const result = await this.get(`team/${teamID}`);
            if (!result.rowCount || result.rowCount === 0) {
                throw new Error(`Team ${teamID} not found`);
            }
            const api_result = { success: true, data: result.rows[0] };
            return api_result
        } catch (err) {
            throw err;
        }
}

  /**
   * Retrieves roster for a specific team.
   * @param teamID - The ID of the team to retrieve the roster for.
   * @returns A Promise that resolves to the roster information.
   * @throws If an error occurs while retrieving the roster information.
   */
  async GetRoster(teamID: number): Promise<any> {
    try {
      const result = await this.get(`roster?teamID=${teamID}`);
      if (!result.rowCount || result.rowCount === 0) {
        throw new Error(`No roster found for team ${teamID}`);
      }
      const api_result = { success: true, data: result.rows };
      return api_result;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Changes the organization of a user.
   * @param userID - The ID of the user.
   * @param orgID - The ID of the organization to be selected.
   * @returns A Promise that resolves to the result of the API call.
   * @throws If an error occurs during the API call.
   */
  async ChangeUserOrg(userID: number, orgID: number): Promise<any> {
    try {
      const result = await this.put(
        `user/${userID}`,
        {},
        { selectedOrgID: orgID }
      );
      this.token = result.token;
      return result;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Changes the team of a user.
   * @param userID - The ID of the user.
   * @param teamID - The ID of the team to assign to the user.
   * @returns A Promise that resolves to the result of the API call.
   * @throws If an error occurs during the API call.
   */
  async ChangeUserTeam(userID: number, teamID: number): Promise<any> {
    try {
      const result = await this.put(
        `user/${userID}`,
        {},
        { selectedTeamID: teamID }
      );
      this.token = result.token;
      return result;
    } catch (err) {
      throw err;
    }
  }

  async ChangeUserOrgTeam(
    userID: number,
    orgID: number,
    teamID: number
  ): Promise<any> {
    try {
      const body = { selectedOrgID: orgID, selectedTeamID: teamID };
      const result = await this.put(`user/${userID}`, {}, body);
      this.token = result.token;
      return result;
    } catch (err) {
      throw err;
    }
  }
  /**
   * Processes the alert with the specified monitor serial number, type, and team ID.
   * @param alertMonitorSerialNumber The serial number of the alert monitor.
   * @param type The type of the alert.
   * @param teamID The ID of the team.
   * @returns A Promise that resolves to the result of the post process operation.
   * @throws If an error occurs during the post process operation.
   */
  async PostProcess(
    alertMonitorSerialNumber: string,
    type: string,
    teamID: number
  ): Promise<any> {
    try {
      const body = {
        alertMonitorSerialNumber: alertMonitorSerialNumber,
        types: [type],
        teamID: teamID,
      };
      return await this.post(`postProcess`, body);
    } catch (err) {
      throw err;
    }
  }

  // don't use this.get() because it inserts a token (don't have one yet, not
  // yet logged in!) and saves the returned token for the other calls to use.
  async login(
    username: string,
    password: string
  ): Promise<AxiosResponse<any, any>> {
    const data = {
      username: username,
      password: password,
    };
    const httpOptions = {
      headers: {
        "x-sbx-auth-origin": ITT_CLIENT,
        "x-sbx-itt-client": ITT_CLIENT,
      },
    };

    try {
      const resp = await http.post(
        `${this.host}/authenticate`,
        data,
        httpOptions
      );
      log.info(`API: POST /login succeeded`);
      this.token = resp.data.token;
      // cacheToken(resp.token);
      return resp;
    } catch (err) {
      const axiosError = err as AxiosError;
      if (axiosError.isAxiosError && 'Network Error' === axiosError.message) {
        // TODO:    err.error.message = `Riddell Equipment Room cannot connect to the ITT server.  Please check your internet connection.`;
      }
      throw err;
    }
  }

  async WhoAmI(): Promise<any> {
    try {
      return await this.get('whoami');
    } catch (err) {
      throw err;
    }
  }

    async logout(): Promise<void> {
        try {
            await this.post('logout');
        } catch (err) {
            console.log(`logout failed: ${err}`);
        }
        this.token = 0;
        // clearCachedToken();
    }
    
    get host() {
        return this._host;
    }

  get token() {
    return this._token;
  }

  set token(token: string | number | boolean) {
    this._token = token;
  }

  get hasLogoutHandler() {
    return this._logoutHandler !== null;
  }
}

export default new APIClass();

function queryFromParams(params: Record<string, string | number | boolean>) {
  let queryString = '';
  if (Object.keys(params).length > 0) {
    queryString = `?${Object.keys(params)
      .map((key) => key + '=' + params[key])
      .join('&')}`;
  }
  return queryString;
}
